/**
 * Error Tracking — Sentry Integration
 *
 * Initializes Sentry for error tracking and performance monitoring.
 * DSN is read from the runtime `wecomApi.sentryDsn` global (injected by
 * WordPress via `wp_localize_script`), so the value is never committed
 * to source control.
 *
 * @module errorTracking
 * @package Wecom
 */

import * as Sentry from "@sentry/react";

// ── Configuration ────────────────────────────────────────────────────────────

/**
 * Whether Sentry has been successfully initialized.
 * @type {boolean}
 */
let initialized = false;

/**
 * Initialize Sentry error tracking.
 *
 * Must be called once, as early as possible (before React renders).
 * Silently skips initialization when no DSN is available (local dev).
 *
 * @returns {void}
 */
export function initErrorTracking() {
  if (initialized) return;

  const dsn =
    (typeof window.wecomApi !== "undefined" && window.wecomApi.sentryDsn) ||
    import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    return;
  }

  const environment =
    import.meta.env.VITE_ENV ||
    (window.location.hostname === "localhost" ? "development" : "production");

  const release = import.meta.env.VITE_BUILD_HASH
    ? `wecom-frontend@${import.meta.env.VITE_BUILD_HASH}`
    : undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: true,

    // Sample 100% of errors, 20% of transactions in production
    sampleRate: 1.0,
    tracesSampleRate: environment === "production" ? 0.2 : 1.0,

    // Ignore common non-actionable errors
    ignoreErrors: [
      "ResizeObserver loop",
      "Non-Error promise rejection",
      "Network request failed",
      "Load failed",
      "ChunkLoadError",
    ],

    beforeSend(event) {
      // Strip PII from breadcrumbs (passwords, tokens, credit cards)
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((bc) => {
          if (bc.data && typeof bc.data.url === "string") {
            bc.data.url = bc.data.url.replace(
              /weToken=[^&]+/g,
              "weToken=[REDACTED]"
            );
          }
          return bc;
        });
      }
      return event;
    },
  });

  initialized = true;
}

/**
 * Capture an error manually (for catch blocks outside React).
 *
 * @param {Error} error - The error to capture.
 * @param {Object} [context] - Additional context tags/data.
 * @returns {void}
 */
export function captureError(error, context = {}) {
  if (!initialized) return;

  Sentry.withScope((scope) => {
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context.extra) {
      scope.setExtras(context.extra);
    }
    Sentry.captureException(error);
  });
}

/**
 * Set the current user context for Sentry events.
 *
 * @param {{ id?: string, phone?: string }} user - User info (never send full PII).
 * @returns {void}
 */
export function setErrorTrackingUser(user) {
  if (!initialized) return;
  Sentry.setUser(user ? { id: user.id } : null);
}

// Re-export Sentry's ErrorBoundary for use in mountReactApp
export { Sentry };
